import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";

import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import TableFilters from "../../TableFilters";
import {
  addStatePolicyType,
  editStatePolicyType,
  filterText,
  rowEditPolicyType,
  selectedRowsStatePolicyType,
} from "../atoms/atoms";
import PolicyTypeActionForm from "../forms/PolicyTypeActionForm";
import useDeletePolicyType from "../hooks/useDeletePolicyType";
import usePolicyType from "../hooks/usePolicyType";
import { policyTypeData } from "../types/PolicyTypeInterface";

export default function PolicyTypeTable() {
  //Hooks Call
  const {
    data: policyTypeDataDirect,
    isLoading: isFetching,
    isSuccess: policyTypeSuccess,
  } = usePolicyType();

  const BulkDeletePolicyTypeType = useDeletePolicyType();

  //Atoms Call
  const [actionAddState, setActionAddState] =
    useRecoilState(addStatePolicyType);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStatePolicyType
  );
  const [actionEditState, setActionEditState] =
    useRecoilState(editStatePolicyType);
  const [filterTextState, setFilterTextState] = useRecoilState(filterText);
  const setEditRowData = useSetRecoilState(rowEditPolicyType);
  const userPermissions = useRecoilValue(permissionsSelector);

  //Local States
  const [policyTypeData, setPolicyTypeData] = useState<policyTypeData[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [policyTypeFilteredData, setPolicyTypeFilteredData] = useState<
    policyTypeData[]
  >([]);

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_configureagreement_policytype_add;
  const editPermission =
    PERMISSIONS.permission_utilities_configureagreement_policytype_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_configureagreement_policytype_delete;

  //For filtered data update
  const [policyTypeId, setPolicyTypeId] = useState("");

  useEffect(() => {
    if (policyTypeId != "") {
      if (policyTypeId?.includes(".")) {
        let ids = policyTypeId.split(".");
        const arr = ids.map((id) => Number(id));
        const filteredData = policyTypeData?.filter((x) =>
          arr.includes(x.policyTypeID)
        );
        setPolicyTypeFilteredData(filteredData);
      } else {
        const filteredData = policyTypeData?.filter(
          (x) => x.policyTypeID === Number(policyTypeId)
        );
        setPolicyTypeFilteredData(filteredData);
      }
    }
  }, [policyTypeId, policyTypeData]);

  useEffect(() => {
    if (policyTypeSuccess && policyTypeDataDirect) {
      setPolicyTypeData(policyTypeDataDirect.map((data) => ({ ...data })));
    }
  }, [policyTypeDataDirect, policyTypeSuccess]);

  let location = useLocation();
  const history = useHistory();
  let recordIdFromRecent;

  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setPolicyTypeFilteredData(result);
          setPolicyTypeId(recordIdFromRecent.recordId);
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, [history, recordIdFromRecent]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = () => {
    const ids = selectedRows
      ? selectedRows.map((row) => row.policyTypeID)
      : undefined;
    BulkDeletePolicyTypeType(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Policy Types" : "Policy Type"
    }?`}</div>
  );

  const filteredItems = policyTypeData
    ? policyTypeData?.filter(
        (item) =>
          item.policyTypeName &&
          item.policyTypeName
            .toLowerCase()
            .includes(filterTextState?.trim().toLowerCase())
      )
    : [];

  const matColumns = [
    {
      title: "Policy Type Name",
      field: "policyTypeName",
      customSort: (a, b) =>
        a.policyTypeName && b.policyTypeName
          ? a.policyTypeName.trim().localeCompare(b.policyTypeName.trim())
          : a.policyTypeName
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdOn",
      type: "date" as "date",
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },

    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center mr-5">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="bg-white p-4">
        {actionAddState || actionEditState ? (
          <div>
            <PolicyTypeActionForm />
          </div>
        ) : (
          <>
            <div className="flex flex-row justify-between items-center">
              <TableFilters
                moduleDetails={"PolicyType"}
                textInputName={"PolicyTypeName"}
                columnDetails={"Policy Type Name"}
                filterText={setFilterTextState}
                disabledButton={
                  selectedRows !== undefined && selectedRows.length > 0
                }
                disabledTextBox={
                  selectedRows !== undefined && selectedRows.length > 0
                }
              />

              <div className="">
                {userPermissions.includes(addPermission) ? (
                  <>
                    <AddButton
                      buttonText="New Policy Type"
                      onClick={() => handleAdd()}
                      disabled={
                        selectedRows !== undefined && selectedRows.length > 0
                      }
                    />
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>
            <div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
            </div>
            <div>
              <MaterialTableTS
                data={
                  policyTypeFilteredData?.length > 0
                    ? policyTypeFilteredData
                    : filterTextState === ""
                    ? policyTypeData
                    : filteredItems
                }
                showTitle={true}
                columns={matColumns}
                onSelectionChange={handleSelectedRowsChange}
                selectedRows={selectedRows}
                selection={userPermissions.includes(deletePermission)}
                isLoading={isFetching}
                setConfirmModalOpen={setOpenModalDelete}
              />
            </div>

            <ConfirmBoxDelete
              ModalTitle="Deactivate Policy Type"
              qConfirmation={deleteMessage}
              isOpen={openModalDelete}
              onClose={() => {
                setOpenModalDelete(false);
                setSelectedRows([]);
              }}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </>
        )}
      </div>
    </>
  );
}
